import { getHSLAColor, getUIKitColorHSLA } from "@/helpers/style.helper.ts";
import {
  getReportingTooltipYAlign,
  reportingTooltipCaretPadding,
} from "@/components/reporting/chartPlugins.ts";
import { Box, type ColorName, Stack } from "@skailer-com/uikit";
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  type ChartData,
  ChartDataset,
  type ChartOptions,
  Legend,
  LinearScale,
  Title as TitleChart,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import React, { useMemo, useRef } from "react";
import { Chart, getElementAtEvent } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  TitleChart,
  Tooltip,
  Legend,
  BarController,
  ChartDataLabels
);

export interface BarChartSeries {
  label: string;
  data: (number | null)[];
  // CSS custom property name, e.g. "--color-new".
  color: string;
  formatValue?: (value: number) => string;
}

export interface BarChartProps {
  labels: string[];
  series: BarChartSeries[];
  height?: number;
  showDataLabels?: boolean;
  showLegend?: boolean;
  // Single series only: bars are coloured per category from this palette, not the series colour.
  palette?: readonly ColorName[];
  formatValue?: (value: number) => string;
  onBarClick?: (seriesIndex: number, pointIndex: number) => void;
}

export const BarChart = ({
  labels,
  series,
  height = 236,
  showDataLabels = true,
  showLegend = false,
  palette,
  formatValue,
  onBarClick,
}: BarChartProps) => {
  const chartRef = useRef<ChartJS>(null);

  const data = useMemo<ChartData>(() => {
    const colorByCategory = palette && series.length === 1;
    const datasets: ChartDataset[] = series.map((s) => {
      const backgroundColor = colorByCategory
        ? s.data.map((_, index) => getUIKitColorHSLA(palette![index % palette!.length], 0.5))
        : getHSLAColor(s.color, 0.5);
      const borderColor = colorByCategory
        ? s.data.map((_, index) => getUIKitColorHSLA(palette![index % palette!.length]))
        : getHSLAColor(s.color);
      return {
        type: "bar",
        label: s.label,
        data: s.data,
        categoryPercentage: 0.8,
        borderColor,
        backgroundColor,
        hoverBackgroundColor: borderColor,
        borderRadius: 4,
        yAxisID: "y",
        minBarLength: 1,
        datalabels: {
          display: showDataLabels,
        },
      };
    });
    return { labels, datasets };
  }, [labels, series, showDataLabels, palette]);

  const yAxisMax = useMemo(() => {
    const allValues = series.flatMap((s) => s.data).filter((value) => value !== null);
    if (!allValues.length) return undefined;
    return Math.ceil(Math.max(...allValues) * 1.3);
  }, [series]);

  const hasFormatter = !!formatValue || series.some((s) => s.formatValue);
  const getSeriesFormatter = (datasetIndex: number) =>
    series[datasetIndex]?.formatValue ?? formatValue;

  const options: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 250,
    },
    layout: {
      padding: {
        top: 20,
      },
    },
    plugins: {
      datalabels: {
        anchor: "end",
        align: "top",
        offset: 2,
        color: getHSLAColor("--color-dark", 0.6),
        display: showDataLabels,
        clamp: false,
        clip: false,
        font: {
          size: 11,
        },
        formatter: hasFormatter
          ? (value: number | null, context) => {
              const format = getSeriesFormatter(context.datasetIndex);
              if (!format) return value;
              return value === null ? "-" : format(value);
            }
          : undefined,
      },
      legend: {
        display: showLegend,
        position: "bottom",
        labels: {
          boxWidth: 12,
          boxHeight: 12,
          usePointStyle: true,
          pointStyle: "rectRounded",
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: true,
        position: "nearest",
        intersect: false,
        yAlign: getReportingTooltipYAlign,
        caretPadding: reportingTooltipCaretPadding,
        displayColors: false,
        backgroundColor: "#25272B",
        padding: 8,
        cornerRadius: 8,
        titleFont: {
          size: 12,
          weight: "normal",
        },
        bodyFont: {
          size: 12,
          weight: "bold",
        },
        callbacks: hasFormatter
          ? {
              label: (context) => {
                const format = getSeriesFormatter(context.datasetIndex);
                const value =
                  context.raw === null
                    ? "-"
                    : format
                      ? format(context.raw as number)
                      : context.formattedValue;
                return series.length > 1 ? `${context.dataset.label}: ${value}` : value;
              },
            }
          : undefined,
      },
    },
    hover: {
      intersect: false,
    },
    scales: {
      x: {
        grid: {
          display: true,
          drawTicks: false,
          color: (context) => {
            const totalTicks = context.chart.scales.x.ticks.length;
            if (context.index === 0 || context.index === totalTicks) {
              return "transparent";
            }
            return getHSLAColor("--color-dark", 0.1);
          },
        },
        ticks: {
          padding: 8,
        },
        border: {
          display: true,
          color: getHSLAColor("--color-dark", 0.1),
        },
      },
      y: {
        type: "linear",
        display: false,
        position: "left",
        max: yAxisMax,
        min: 0,
        grid: {
          display: false,
        },
        ticks: {
          display: false,
        },
        border: {
          display: false,
        },
      },
    },
  };

  const handleClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!chartRef.current || !onBarClick) return;
    // Мимо столбца берём ближайший по X: так клик ловит подпись и отступ
    const direct = getElementAtEvent(chartRef.current, event);
    const element = direct.length
      ? direct
      : chartRef.current.getElementsAtEventForMode(
          event.nativeEvent,
          "index",
          { intersect: false },
          true
        );
    if (element.length > 0) {
      const { datasetIndex, index } = element[0];
      onBarClick(datasetIndex, index);
    }
  };

  return (
    <Stack gap={"md"}>
      <Box style={{ height }}>
        <Chart
          ref={chartRef}
          className={onBarClick ? "cursor-pointer" : undefined}
          onClick={handleClick}
          type={"bar"}
          plugins={[ChartDataLabels]}
          options={options}
          data={data}
        />
      </Box>
    </Stack>
  );
};
